import type { Breadcrumb } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BellOff, Check, RotateCcw } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { AssigneePicker } from '@/components/assignee';
import { CommentThread } from '@/components/comments';
import { LevelBadge } from '@/components/level-badge';
import { Sparkline } from '@/components/sparkline';
import { StacktraceView } from '@/components/stacktrace';
import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import { absoluteTime, compactNumber, relativeTime, splitTitle } from '@/lib/format';
import { cn } from '@/lib/utils';

export function IssueDetailPage() {
  const { projectId = '', issueId = '' } = useParams();
  const { client } = useAuth();
  const queryClient = useQueryClient();

  const issue = useQuery({ queryKey: ['issue', issueId], queryFn: () => client.getIssue(issueId) });
  const event = useQuery({
    queryKey: ['issue-latest', issueId],
    queryFn: () => client.latestEvent(issueId),
    // A brand-new issue may briefly have no stored event yet.
    retry: 1,
  });
  const stats = useQuery({
    queryKey: ['issue-stats-detail', issueId],
    queryFn: () => client.issueStats(issueId, 24 * 7),
  });

  const setStatus = useMutation({
    mutationFn: (status: 'resolved' | 'ignored' | 'unresolved') =>
      client.updateIssue(issueId, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueId] });
      void queryClient.invalidateQueries({ queryKey: ['issues', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['issue-activity', issueId] });
    },
  });

  if (issue.isLoading) return <ListSkeleton rows={4} />;
  if (issue.isError) return <ErrorState error={issue.error} onRetry={() => void issue.refetch()} />;
  if (!issue.data) return null;

  const { type, message } = splitTitle(issue.data.title);
  const exception = event.data?.payload.exception?.[0];
  const resolved = issue.data.status === 'resolved';

  return (
    <div className="space-y-5">
      <Link
        to={`/projects/${projectId}/issues`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to issues
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-baseline gap-x-2 wrap-anywhere">
            {type && <h1 className="text-lg font-semibold sm:text-xl">{type}</h1>}
            <span
              className={cn(
                'text-base sm:text-lg',
                type ? 'text-muted-foreground' : 'font-semibold',
              )}
            >
              {message}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <LevelBadge level={issue.data.level} />
            {issue.data.culprit && (
              <span className="font-mono text-xs wrap-anywhere">{issue.data.culprit}</span>
            )}
            {issue.data.last_release && (
              <Badge variant="outline">release {issue.data.last_release}</Badge>
            )}
            {event.data?.environment && <Badge variant="muted">{event.data.environment}</Badge>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AssigneePicker
            projectId={projectId}
            issueId={issueId}
            assigneeId={issue.data.assignee_id}
          />
          {resolved ? (
            <Button variant="outline" onClick={() => setStatus.mutate('unresolved')}>
              <RotateCcw />
              Reopen
            </Button>
          ) : (
            <Button onClick={() => setStatus.mutate('resolved')} disabled={setStatus.isPending}>
              <Check />
              Resolve
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setStatus.mutate('ignored')}
            disabled={setStatus.isPending || issue.data.status === 'ignored'}
          >
            <BellOff />
            Ignore
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Events" value={compactNumber(issue.data.times_seen)} />
        <Stat label="Users affected" value={compactNumber(issue.data.users_affected)} />
        <Stat
          label="First seen"
          value={relativeTime(issue.data.first_seen)}
          title={absoluteTime(issue.data.first_seen)}
        />
        <Stat
          label="Last seen"
          value={relativeTime(issue.data.last_seen)}
          title={absoluteTime(issue.data.last_seen)}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Events over the last 7 days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Sparkline buckets={stats.data ?? []} width={1000} height={72} className="w-full" />
        </CardContent>
      </Card>

      {event.isError && (
        <ErrorState error={new Error('The latest event for this issue could not be loaded.')} />
      )}

      {event.data && (
        <Tabs defaultValue="stacktrace">
          <TabsList>
            <TabsTrigger value="stacktrace">Stack trace</TabsTrigger>
            <TabsTrigger value="breadcrumbs">
              Breadcrumbs
              {event.data.payload.breadcrumbs?.length ? (
                <span className="ml-1.5 text-muted-foreground">
                  {event.data.payload.breadcrumbs.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="tags">Tags</TabsTrigger>
            <TabsTrigger value="context">Context</TabsTrigger>
          </TabsList>

          <TabsContent value="stacktrace">
            {exception ? (
              <div className="space-y-3">
                <div className="text-sm wrap-anywhere">
                  <span className="font-medium">{exception.type}</span>
                  {exception.value && (
                    <span className="text-muted-foreground">: {exception.value}</span>
                  )}
                  {exception.mechanism?.handled === false && (
                    <Badge variant="destructive" className="ml-2">
                      unhandled
                    </Badge>
                  )}
                </div>
                <StacktraceView exception={exception} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                This event has no exception; it was reported as a message.
              </p>
            )}
          </TabsContent>

          <TabsContent value="breadcrumbs">
            <BreadcrumbList breadcrumbs={event.data.payload.breadcrumbs ?? []} />
          </TabsContent>

          <TabsContent value="tags">
            <KeyValueGrid values={event.data.tags} />
          </TabsContent>

          <TabsContent value="context">
            <div className="space-y-4">
              {event.data.payload.user && (
                <Section title="User">
                  <KeyValueGrid
                    values={Object.fromEntries(
                      Object.entries(event.data.payload.user).map(([k, v]) => [k, String(v)]),
                    )}
                  />
                </Section>
              )}
              {event.data.payload.request && (
                <Section title="Request">
                  <KeyValueGrid
                    values={{
                      url: event.data.payload.request.url ?? '—',
                      method: event.data.payload.request.method ?? '—',
                    }}
                  />
                </Section>
              )}
              <Section title="Raw payload">
                <pre className="scrollbar-thin max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
                  {JSON.stringify(event.data.payload, null, 2)}
                </pre>
              </Section>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Last on the page: the event is what you came to read, the discussion
          is what you leave behind. */}
      <CommentThread projectId={projectId} issueId={issueId} />
    </div>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <Card>
      <CardContent className="p-3 sm:p-4" title={title}>
        <p className="truncate text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 truncate text-xl font-semibold tabular-nums sm:text-2xl">{value}</p>
      </CardContent>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {children}
    </div>
  );
}

/**
 * The trail of events leading to the crash, newest last so it reads
 * chronologically like a story.
 */
function BreadcrumbList({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) {
  if (breadcrumbs.length === 0) {
    return <p className="text-sm text-muted-foreground">No breadcrumbs were recorded.</p>;
  }

  return (
    <div className="overflow-hidden rounded-md border border-border">
      {breadcrumbs.map((crumb, index) => (
        <div
          key={index}
          className="flex items-start gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0 sm:gap-3"
        >
          <Badge variant="muted" className="mt-0.5 shrink-0 font-mono text-[10px]">
            {crumb.category ?? 'default'}
          </Badge>
          <span className="min-w-0 flex-1 wrap-anywhere">{crumb.message ?? '—'}</span>
          {crumb.timestamp && (
            <span
              className="shrink-0 text-xs text-muted-foreground"
              title={absoluteTime(crumb.timestamp)}
            >
              {relativeTime(crumb.timestamp)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function KeyValueGrid({ values }: { values: Record<string, string> }) {
  const entries = Object.entries(values);
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing recorded.</p>;
  }

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-baseline gap-2 border-b border-border/50 py-1">
          <dt className="w-28 shrink-0 truncate font-mono text-xs text-muted-foreground sm:w-40">
            {key}
          </dt>
          <dd className="min-w-0 flex-1 truncate font-mono text-xs" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export { Separator };
